import { ArrowPathIcon, CheckCircleIcon, LinkIcon } from '@heroicons/react/24/outline'
import { useTranslation } from 'react-i18next'

import type { AccountConnection } from '@/api'
import { GoogleIcon } from '@/components/GoogleIcon'
import { MicrosoftIcon } from '@/components/MicrosoftIcon'

type ConnectionRowProps = {
    connection: AccountConnection
    pending: boolean
    disabled: boolean
    onConnect: () => void
}

export const ConnectionRow = ({ connection, pending, disabled, onConnect }: ConnectionRowProps) => {
    const { t } = useTranslation()
    const providerName = t(`connections.providers.${connection.provider}`)

    return (
        <li className="flex flex-col gap-4 p-6 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex min-w-0 items-center gap-4">
                <div className="border-account-divider bg-account-muted shrink-0 rounded-full border p-2.5">
                    {connection.provider === 'google' ? <GoogleIcon /> : <MicrosoftIcon />}
                </div>
                <div className="min-w-0">
                    <h3 className="text-account-strong font-bold">{providerName}</h3>
                    {connection.connected && connection.email && (
                        <p className="text-account-secondary mt-1 break-all text-sm">{connection.email}</p>
                    )}
                    {!connection.enabled && (
                        <p className="text-account-secondary mt-1 text-sm">{t('connections.unavailable')}</p>
                    )}
                </div>
            </div>

            <div className="flex shrink-0 justify-end">
                {connection.connected ? (
                    <span className="text-account-success inline-flex items-center gap-2 text-sm font-semibold">
                        <CheckCircleIcon className="h-5 w-5" aria-hidden="true" />
                        {t('connections.connected')}
                    </span>
                ) : (
                    <button
                        type="button"
                        onClick={onConnect}
                        disabled={disabled}
                        aria-busy={pending}
                        aria-label={t('connections.connectProvider', { provider: providerName })}
                        className="border-account-border bg-account-surface text-account-primary hover:bg-account-muted inline-flex items-center gap-2 whitespace-nowrap rounded-lg border px-5 py-2 font-semibold transition disabled:cursor-not-allowed disabled:opacity-60"
                    >
                        {pending ? (
                            <ArrowPathIcon className="h-4 w-4 animate-spin" aria-hidden="true" />
                        ) : (
                            <LinkIcon className="h-4 w-4" aria-hidden="true" />
                        )}
                        {pending ? t('connections.connecting') : t('connections.connect')}
                    </button>
                )}
            </div>
        </li>
    )
}
